import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, firstValueFrom, take } from 'rxjs';

import { isAccountStatusProblem } from '@core/models/account-state.model';
import { KeyringTenant } from '@core/models/api/keyring/tenant.model';
import { AUTH_ROUTE, MODULE_ROUTE, SECRET_ROUTE } from '@core/models/routes.model';
import { TenantApi, TenantLoadError } from '@core/services/api/tenant.api';
import { AuthStateService } from '@core/services/auth-state.service';
import { safeReturnUrl } from '@shared/helpers/return-url.helper';

export interface AuthSession {
    loggedIn: boolean;
    tenant: KeyringTenant | undefined;
    /** Set when loading the tenant failed: the account state is unknown, so it is neither onboarded nor waiting for an invitation */
    tenantError: TenantLoadError | undefined;
}

/**
 * Session lookups and post-authentication redirects shared by the route guards.
 *
 * This service answers "what does the session contain" and performs the
 * navigations; deciding *who* may enter a route is left to the guards.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthSessionService {
    private readonly auth = inject(AuthService);
    private readonly tenantApi = inject(TenantApi);
    private readonly authStateService = inject(AuthStateService);
    private readonly router = inject(Router);

    /** The page the user asked for before logging in, brought back by Auth0 in the login's `appState` */
    private returnUrl: string | undefined;

    constructor() {
        // Auth0 replays the appState of the login callback; it never emits on a normal page load
        this.auth.appState$.pipe(take(1)).subscribe((appState: { target?: unknown } | undefined) => {
            this.returnUrl = safeReturnUrl(appState?.target);
        });
    }

    /** The page to return to after this login, while it has not been navigated to yet. */
    public pendingReturnUrl(): string | undefined {
        return this.returnUrl;
    }

    /**
     * True while the Auth0 callback (`?code=&state=`) is still being processed by AppComponent.
     * Guards must block navigation until that has settled.
     */
    public isHandlingCallback(): boolean {
        const urlParams = new URLSearchParams(globalThis.location.search);

        return urlParams.has('code') && urlParams.has('state');
    }

    /**
     * Resolves once Auth0 and the app initialization have settled, so token/user/tenant
     * are cached before a guard inspects them.
     */
    public async resolveSession(): Promise<AuthSession> {
        // Wait for Auth0 to finish loading before checking authentication state
        // This prevents race conditions where isAuthenticated$ emits false prematurely
        await firstValueFrom(this.auth.isLoading$.pipe(filter(loading => !loading)));

        // Wait for AppComponent initialization to complete
        // This ensures token/user/tenant are cached before we check them
        await firstValueFrom(this.authStateService.waitForInitialization());

        return {
            loggedIn: await firstValueFrom(this.auth.isAuthenticated$),
            tenant: this.tenantApi.getTenant(),
            tenantError: this.tenantApi.loadError(),
        };
    }

    /**
     * An account is only usable once its tenant has a root folder to land in.
     * Anything else means the invitation has not been activated yet.
     */
    public isOnboarded(tenant: KeyringTenant | undefined): boolean {
        return Boolean(tenant?.rootId);
    }

    /**
     * The tenant request was refused because the account is not active (invited, imported,
     * disabled...) or its status is unknown. That user is waiting for activation and belongs on
     * the onboarding page, not on the session error page: retrying will not help them.
     */
    public isAccountStatusError(tenantError: TenantLoadError | undefined): boolean {
        return isAccountStatusProblem(tenantError?.problem);
    }

    /**
     * Whether a signed-in session should see the onboarding page: the account is not active
     * according to the API, or it has no workspace to land in yet.
     */
    public isAwaitingActivation(tenant: KeyringTenant | undefined, tenantError: TenantLoadError | undefined): boolean {
        return tenantError ? this.isAccountStatusError(tenantError) : !this.isOnboarded(tenant);
    }

    /**
     * Sends an authenticated user to the page they asked for before logging in, or else to their
     * workspace. The session error page (tenant could not be loaded) and onboarding (account not
     * activated yet) come first. The page to return to is used once.
     */
    public async navigateAfterLogin(tenant: KeyringTenant | undefined): Promise<void> {
        const tenantError = this.tenantApi.loadError();

        if (tenantError && !this.isAccountStatusError(tenantError)) {
            await this.navigateToSessionError();

            return;
        }

        if (this.isAwaitingActivation(tenant, tenantError)) {
            await this.navigateToOnboarding();

            return;
        }

        const returnUrl = this.returnUrl;
        if (returnUrl) {
            this.returnUrl = undefined;
            await this.router.navigateByUrl(returnUrl);

            return;
        }

        const queryParams = this.router.getCurrentNavigation()?.extras.queryParams;
        await this.router.navigate(
            [MODULE_ROUTE.SECRETS, SECRET_ROUTE.FOLDERS, tenant?.rootId],
            { queryParams }
        );
    }

    /** Opens the login page; a safe `returnUrl` goes along so the login can come back to it. */
    public navigateToLogin(returnUrl?: string): Promise<boolean> {
        const target = safeReturnUrl(returnUrl);
        if (target) {
            return this.router.navigate([MODULE_ROUTE.AUTH, AUTH_ROUTE.LOGIN], { queryParams: { returnUrl: target } });
        }

        return this.router.navigate([MODULE_ROUTE.AUTH, AUTH_ROUTE.LOGIN]);
    }

    public navigateToOnboarding(): Promise<boolean> {
        return this.router.navigate([MODULE_ROUTE.AUTH, AUTH_ROUTE.ONBOARDING]);
    }

    public navigateToSessionError(): Promise<boolean> {
        return this.router.navigate([MODULE_ROUTE.AUTH, AUTH_ROUTE.SESSION_ERROR]);
    }
}
